export interface OtherTagCategory {
  key: string;
  translationKey: string;
  tags: string[];
}

export const EMOTION_CATEGORY_KEYS: readonly string[] = [
  "angry",
  "disgusted",
  "fearful",
  "happy",
  "neutral",
  "sad",
  "surprised",
];

export const EMOTION_SUGGESTION_MAP: Readonly<Record<string, string[]>> = {
  angry: [
    "[angry]",
    "[furious]",
    "[enraged]",
    "[seething]",
    "[outraged]",
    "[irritated]",
    "[frustrated]",
    "[fuming]",
    "[bitter]",
    "[hostile]",
  ],
  disgusted: [
    "[disgusted]",
    "[revolted]",
    "[appalled]",
    "[repulsed]",
    "[contemptuous]",
    "[disdainful]",
    "[nauseated]",
    "[scornful]",
    "[with disgust]",
    "[grimacing]",
  ],
  fearful: [
    "[fearful]",
    "[terrified]",
    "[panicked]",
    "[nervously]",
    "[anxiously]",
    "[trembling]",
    "[petrified]",
    "[dreading]",
    "[with dread]",
    "[in a shaking voice]",
  ],
  happy: [
    "[happy]",
    "[joyful]",
    "[cheerfully]",
    "[excited]",
    "[delighted]",
    "[elated]",
    "[gleefully]",
    "[playfully]",
    "[enthusiastically]",
    "[warmly]",
  ],
  neutral: [
    "[neutral]",
    "[flatly]",
    "[calmly]",
    "[deadpan]",
    "[matter-of-factly]",
    "[evenly]",
    "[composed]",
    "[indifferent]",
    "[monotone]",
    "[stoic]",
  ],
  sad: [
    "[sad]",
    "[sorrowful]",
    "[tearfully]",
    "[mournful]",
    "[dejected]",
    "[heartbroken]",
    "[melancholic]",
    "[despondent]",
    "[forlorn]",
    "[quietly]",
  ],
  surprised: [
    "[surprised]",
    "[shocked]",
    "[astonished]",
    "[gasps]",
    "[stunned]",
    "[wide-eyed]",
    "[amazed]",
    "[bewildered]",
    "[startled]",
    "[taken aback]",
  ],
};

export const EMOTION_CANONICAL_MAP: Readonly<Record<string, string>> = {
  // angry
  anger: "angry",
  angry: "angry",
  bitter: "angry",
  enraged: "angry",
  frustrated: "angry",
  fuming: "angry",
  furious: "angry",
  hostile: "angry",
  hostility: "angry",
  irritated: "angry",
  outraged: "angry",
  seething: "angry",
  // disgusted
  appalled: "disgusted",
  contempt: "disgusted",
  contemptuous: "disgusted",
  disdainful: "disgusted",
  disgust: "disgusted",
  disgusted: "disgusted",
  grimacing: "disgusted",
  nauseated: "disgusted",
  repulsed: "disgusted",
  revolted: "disgusted",
  scornful: "disgusted",
  "with disgust": "disgusted",
  // fearful
  afraid: "fearful",
  anxious: "fearful",
  anxiously: "fearful",
  dreading: "fearful",
  fear: "fearful",
  fearful: "fearful",
  "in a shaking voice": "fearful",
  nervous: "fearful",
  nervously: "fearful",
  panic: "fearful",
  panicked: "fearful",
  petrified: "fearful",
  terrified: "fearful",
  trembling: "fearful",
  "with dread": "fearful",
  // happy
  cheerfully: "happy",
  delight: "happy",
  delighted: "happy",
  elated: "happy",
  enthusiastically: "happy",
  excited: "happy",
  excitement: "happy",
  gleefully: "happy",
  happiness: "happy",
  happy: "happy",
  joy: "happy",
  joyful: "happy",
  playfully: "happy",
  warmly: "happy",
  // neutral
  calm: "neutral",
  calmly: "neutral",
  composed: "neutral",
  deadpan: "neutral",
  evenly: "neutral",
  flatly: "neutral",
  indifferent: "neutral",
  "matter-of-factly": "neutral",
  monotone: "neutral",
  neutral: "neutral",
  stoic: "neutral",
  // sad
  dejected: "sad",
  despondent: "sad",
  forlorn: "sad",
  heartbroken: "sad",
  melancholic: "sad",
  melancholy: "sad",
  mournful: "sad",
  quietly: "sad",
  sad: "sad",
  sadness: "sad",
  sorrowful: "sad",
  tearfully: "sad",
  // surprised
  amazed: "surprised",
  astonished: "surprised",
  bewildered: "surprised",
  gasps: "surprised",
  shocked: "surprised",
  startled: "surprised",
  stunned: "surprised",
  surprise: "surprised",
  surprised: "surprised",
  "taken aback": "surprised",
  "wide-eyed": "surprised",
};

export const OTHER_TAG_CATEGORIES: readonly OtherTagCategory[] = [
  {
    key: "archetypesRoles",
    translationKey: "replies.otherTags.categories.archetypesRoles",
    tags: [
      "[pirate voice]",
      "[evil scientist voice]",
      "[childlike tone]",
      "[deep voice]",
      "[robotic tone]",
    ],
  },
  {
    key: "speechStyles",
    translationKey: "replies.otherTags.categories.speechStyles",
    tags: ["[dramatic]", "[sarcastically]", "[matter-of-fact]", "[whiny]"],
  },
  {
    key: "genreCues",
    translationKey: "replies.otherTags.categories.genreCues",
    tags: ["[fantasy narrator]", "[sci-fi AI voice]", "[classic film noir]"],
  },
  {
    key: "emotionalStates",
    translationKey: "replies.otherTags.categories.emotionalStates",
    tags: ["[excited]", "[nervous]", "[frustrated]", "[sorrowful]", "[calm]"],
  },
  {
    key: "reactions",
    translationKey: "replies.otherTags.categories.reactions",
    tags: ["[sigh]", "[laughs]", "[gulps]", "[gasps]", "[whispers]"],
  },
  {
    key: "cognitiveBeats",
    translationKey: "replies.otherTags.categories.cognitiveBeats",
    tags: ["[pauses]", "[hesitates]", "[stammers]", "[resigned tone]"],
  },
  {
    key: "toneCues",
    translationKey: "replies.otherTags.categories.toneCues",
    tags: ["[cheerfully]", "[flatly]", "[deadpan]", "[playfully]"],
  },
  {
    key: "storyBeats",
    translationKey: "replies.otherTags.categories.storyBeats",
    tags: ["[pause]", "[continues softly]", "[hesitates]", "[resigned]"],
  },
  {
    key: "toneSetting",
    translationKey: "replies.otherTags.categories.toneSetting",
    tags: [
      "[dramatic tone]",
      "[lighthearted]",
      "[reflective]",
      "[serious tone]",
    ],
  },
  {
    key: "narratorPov",
    translationKey: "replies.otherTags.categories.narratorPov",
    tags: ["[awe]", "[sarcastic tone]", "[wistful]", "[matter-of-fact]"],
  },
  {
    key: "rhythmFlow",
    translationKey: "replies.otherTags.categories.rhythmFlow",
    tags: ["[slows down]", "[rushed]", "[emphasized]"],
  },
  {
    key: "turnTakingCues",
    translationKey: "replies.otherTags.categories.turnTakingCues",
    tags: ["[interrupting]", "[overlapping]", "[cuts in]"],
  },
  {
    key: "emotionalShifts",
    translationKey: "replies.otherTags.categories.emotionalShifts",
    tags: ["[excited]", "[annoyed]", "[flustered]", "[casual]"],
  },
  {
    key: "rhythmicFlow",
    translationKey: "replies.otherTags.categories.rhythmicFlow",
    tags: ["[fast-paced]", "[hesitates]", "[pause]", "[drawn out]"],
  },
  {
    key: "pausesBreaks",
    translationKey: "replies.otherTags.categories.pausesBreaks",
    tags: ["[pause]", "[breathes]", "[continues after a beat]"],
  },
  {
    key: "speedCues",
    translationKey: "replies.otherTags.categories.speedCues",
    tags: ["[rushed]", "[slows down]", "[deliberate]", "[rapid-fire]"],
  },
  {
    key: "hesitationRhythm",
    translationKey: "replies.otherTags.categories.hesitationRhythm",
    tags: ["[stammers]", "[drawn out]", "[repeats]", "[timidly]"],
  },
  {
    key: "emphasis",
    translationKey: "replies.otherTags.categories.emphasis",
    tags: ["[emphasized]", "[stress on next word]", "[understated]"],
  },
  {
    key: "globalEnglishVariants",
    translationKey: "replies.otherTags.categories.globalEnglishVariants",
    tags: [
      "[American accent]",
      "[British accent]",
      "[Australian accent]",
      "[Indian English]",
      "[Irish accent]",
      "[Southern US accent]",
      "[New York accent]",
      "[Scottish accent]",
      "[French accent]",
      "[German accent]",
      "[Spanish accent]",
      "[Italian accent]",
      "[Russian accent]",
    ],
  },
];
